/**
 * Pure store mutations: `(store, …) -> store`. Records already carry canonical
 * units (km / litres / kWh) and the store's display currency — the UI converts
 * before calling these. Kept pure so edits are unit-tested independently of
 * IndexedDB, and so export always reflects exactly what's in memory.
 *
 * Only MANUAL spendings are stored; fuel/charge/tyre mirrors stay derived.
 */
import type {
  CanonicalStore,
  Car,
  FuelLog,
  Spending,
  TankEstimate,
  Trip,
} from "./model.ts";

export function newId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function upsertById<T extends { id: string }>(list: T[], item: T): T[] {
  const i = list.findIndex((x) => x.id === item.id);
  if (i === -1) return [...list, item];
  const copy = list.slice();
  copy[i] = item;
  return copy;
}

export function upsertFuelLog(store: CanonicalStore, log: FuelLog): CanonicalStore {
  return { ...store, fuelLogs: upsertById(store.fuelLogs, { ...log, currency: store.profile.displayCurrency }) };
}
export function deleteFuelLog(store: CanonicalStore, id: string): CanonicalStore {
  return { ...store, fuelLogs: store.fuelLogs.filter((l) => l.id !== id) };
}

export function upsertSpending(store: CanonicalStore, s: Spending): CanonicalStore {
  // Never store a mirror: force manual (fuelLogId null) + display currency.
  const entry: Spending = { ...s, fuelLogId: null, currency: store.profile.displayCurrency };
  return { ...store, spendings: upsertById(store.spendings, entry) };
}
export function deleteSpending(store: CanonicalStore, id: string): CanonicalStore {
  return { ...store, spendings: store.spendings.filter((s) => s.id !== id) };
}

export function upsertTrip(store: CanonicalStore, t: Trip): CanonicalStore {
  return { ...store, trips: upsertById(store.trips, t) };
}
export function deleteTrip(store: CanonicalStore, id: string): CanonicalStore {
  return { ...store, trips: store.trips.filter((t) => t.id !== id) };
}

export function upsertCar(store: CanonicalStore, car: Car): CanonicalStore {
  return { ...store, cars: upsertById(store.cars, { ...car, purchaseCurrency: store.profile.displayCurrency }) };
}
export function deleteCar(store: CanonicalStore, id: string): CanonicalStore {
  // Remove the car and everything that references it.
  return {
    ...store,
    cars: store.cars.filter((c) => c.id !== id),
    fuelLogs: store.fuelLogs.filter((l) => l.carId !== id),
    spendings: store.spendings.filter((s) => s.carId !== id),
    tyres: store.tyres.filter((t) => t.carId !== id),
    tyreSets: store.tyreSets.filter((t) => t.carId !== id),
    trips: store.trips.filter((t) => t.carId !== id),
    maintenanceReminders: store.maintenanceReminders.filter((m) => m.carId !== id),
  };
}

export function saveTankEstimate(
  store: CanonicalStore,
  carId: string,
  battery: boolean,
  est: TankEstimate,
): CanonicalStore {
  return {
    ...store,
    cars: store.cars.map((c) =>
      c.id === carId ? (battery ? { ...c, chargeEstimate: est } : { ...c, tankEstimate: est }) : c,
    ),
  };
}

export function setDisplayCurrency(store: CanonicalStore, currency: string): CanonicalStore {
  // Only relabels the display tag; does NOT convert amounts (that would need rates).
  return { ...store, profile: { ...store.profile, displayCurrency: currency } };
}

/** Convert every monetary amount from `from` to `to` using the given rate.
 *  `rate` = 1 unit of `from` expressed in `to` (e.g. from=USD,to=EUR, rate=0.92).
 */
export function convertStoreCurrency(store: CanonicalStore, from: string, to: string, rate: number): CanonicalStore {
  const mul = (v: number | null | undefined) => v != null ? Math.round(v * rate * 100) / 100 : v;
  return {
    ...store,
    profile: { ...store.profile, displayCurrency: to },
    cars: store.cars.map((c) => ({
      ...c,
      purchasePrice: mul(c.purchasePrice),
      purchaseCurrency: to,
      insuranceAmount: mul(c.insuranceAmount),
      roadTaxAmount: mul(c.roadTaxAmount),
    })),
    fuelLogs: store.fuelLogs.map((l) => ({
      ...l,
      totalCost: mul(l.totalCost)!,
      fuelCost: mul(l.fuelCost),
      energyCost: mul(l.energyCost),
      currency: to,
    })),
    spendings: store.spendings.map((s) => ({
      ...s,
      amount: mul(s.amount)!,
      currency: to,
    })),
    trips: store.trips.map((t) => ({
      ...t,
      fuelCost: mul(t.fuelCost),
      tollCost: mul(t.tollCost),
      parkingCost: mul(t.parkingCost),
      mileageRate: mul(t.mileageRate),
    })),
    tyres: store.tyres.map((t) => ({ ...t, purchasePrice: mul(t.purchasePrice) })),
    tyreSets: store.tyreSets.map((ts) => ({ ...ts, purchasePrice: mul(ts.purchasePrice) })),
  };
}
export function setUnitSystem(store: CanonicalStore, unitSystem: "metric" | "imperial"): CanonicalStore {
  return { ...store, profile: { ...store.profile, unitSystem } };
}
